if (!customElements.get('bambu-cookie-consent')) {
  class BambuCookieConsent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.privacy = null;
      this.saving = false;
      this.openedBy = null;
      this.preferences = this.querySelector('.bambu-cookie__preferences');
      this.onSettings = (event) => {
        const button = event.target.closest('[data-bambu-cookie-settings]');
        if (!button) return;
        this.openedBy = button;
        this.open(true);
      };
      this.onConsent = () => {
        if (!this.privacy || this.saving) return;
        this.updateGoogle(this.privacy.currentVisitorConsent());
        // A choice collected by Shopify's banner also releases the queue.
        if (this.hasChoice() && !this.isOpen) this.setBlocking(false);
      };
      document.addEventListener('click', this.onSettings);
      document.addEventListener('visitorConsentCollected', this.onConsent);
      this.onAction = (event) => {
        const button = event.target.closest('[data-consent]');
        if (!button || this.saving) return;
        const action = button.dataset.consent;
        if (action === 'customize') {
          this.showPreferences();
          return;
        }
        const consent = {};
        ['preferences', 'analytics', 'marketing'].forEach((key) => {
          consent[key] = action === 'accept' || (action === 'save' && this.querySelector(`[name="${key}"]`).checked);
        });
        this.save(consent);
      };
      this.addEventListener('click', this.onAction);
      this.loadPrivacy();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onSettings);
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      this.removeEventListener('click', this.onAction);
      clearTimeout(this.loadTimer);
      clearTimeout(this.saveTimer);
      this.initialized = false;
    }

    setBlocking(blocking) {
      window.BambuPrivacy.blocking = blocking;
      if (!blocking) window.BambuPrivacy.releasedAt = Date.now();
      document.dispatchEvent(new CustomEvent('bambu:privacy-state', { detail: { blocking } }));
    }

    hasChoice() {
      const consent = this.privacy.currentVisitorConsent();
      return ['preferences', 'analytics', 'marketing'].every((key) => consent[key] === 'yes' || consent[key] === 'no');
    }

    loadPrivacy(attempt = 0) {
      if (!window.Shopify?.loadFeatures) {
        if (attempt < 40) {
          this.loadTimer = setTimeout(() => this.loadPrivacy(attempt + 1), 100);
          return;
        }
        this.showError();
        this.open();
        return;
      }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
        if (!this.isConnected) return;
        if (error || !window.Shopify.customerPrivacy) {
          this.showError();
          this.open();
          return;
        }
        this.privacy = window.Shopify.customerPrivacy;
        this.querySelector('[data-consent-error]').hidden = true;
        this.updateGoogle(this.privacy.currentVisitorConsent());
        if (this.hasChoice()) {
          this.setBlocking(false);
        } else {
          this.open();
        }
      });
    }

    open(customize = false) {
      this.setBlocking(true);
      this.isOpen = true;
      this.hidden = false;
      if (this.privacy) {
        const consent = this.privacy.currentVisitorConsent();
        ['preferences', 'analytics', 'marketing'].forEach((key) => {
          this.querySelector(`[name="${key}"]`).checked = consent[key] === 'yes';
        });
      }
      if (customize) this.showPreferences();
    }

    showPreferences() {
      this.preferences.hidden = false;
      this.querySelector('[data-consent="customize"]').hidden = true;
      this.querySelector('[data-consent="customize"]').setAttribute('aria-expanded', 'true');
      this.querySelector('[data-consent="save"]').hidden = false;
      this.querySelector('[name="preferences"]').focus({ preventScroll: true });
    }

    showError() {
      this.querySelector('[data-consent-error]').hidden = false;
    }

    save(consent) {
      if (!this.privacy) {
        this.showError();
        this.loadPrivacy();
        return;
      }
      this.saving = true;
      this.querySelector('[data-consent-error]').hidden = true;
      this.querySelectorAll('[data-consent]').forEach((button) => { button.disabled = true; });
      const failed = () => {
        this.saving = false;
        this.querySelectorAll('[data-consent]').forEach((button) => { button.disabled = false; });
        this.showError();
      };
      this.saveTimer = setTimeout(failed, 10000);
      // Consent is recorded only after an explicit visitor action.
      this.privacy.setTrackingConsent(consent, (error) => {
        clearTimeout(this.saveTimer);
        if (error?.error || error instanceof Error || !this.hasChoice()) {
          failed();
          return;
        }
        this.saving = false;
        this.querySelectorAll('[data-consent]').forEach((button) => { button.disabled = false; });
        this.updateGoogle(this.privacy.currentVisitorConsent());
        this.hidden = true;
        this.isOpen = false;
        this.preferences.hidden = true;
        this.querySelector('[data-consent="customize"]').hidden = false;
        this.querySelector('[data-consent="customize"]').setAttribute('aria-expanded', 'false');
        this.querySelector('[data-consent="save"]').hidden = true;
        if (this.openedBy?.isConnected) this.openedBy.focus({ preventScroll: true });
        this.setBlocking(false);
      });
    }

    updateGoogle(consent) {
      const permission = (key) => consent[key] === 'yes' ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        analytics_storage: permission('analytics'),
        ad_storage: permission('marketing'),
        ad_user_data: permission('marketing'),
        ad_personalization: permission('marketing'),
        functionality_storage: permission('preferences'),
        personalization_storage: permission('preferences')
      });
    }
  }
  customElements.define('bambu-cookie-consent', BambuCookieConsent);
}
